import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Trash2, RotateCcw, AlertTriangle, ArrowLeft } from "lucide-react";
import { getTrashedProperties, restoreProperty } from "../../../api/propertyManagement";
import { getTrashedCollingsProperties, restoreCollingsProperty } from "../../../api/collingsProperties";
import ConfirmModal from "../../../components/common/ConfirmModal";
import toast from "react-hot-toast";
import { hasAdminAccess } from "../../../utils/roleAccess";

// My Listings (property_management.Property) and Collings Properties
// (property_crawler.RealEstateProperty) are two genuinely separate datasets -
// each can be deleted independently and has its own restore endpoint - but
// showing them as two separate Trash nav entries read as "the same thing
// twice" (CG feedback, 2026-08-13: "why do we need 3 tabs... please remove
// one"). Merged into a single list here; the source is kept per-row only to
// route Restore to the right endpoint.
export default function PropertyTrashPage() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [trashed, setTrashed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  useEffect(() => {
    if (!hasAdminAccess(user)) {
      navigate("/properties");
      return;
    }
    fetchTrash();
  }, []);

  const fetchTrash = async () => {
    setIsLoading(true);
    try {
      const [myListingsRes, collingsRes] = await Promise.all([
        getTrashedProperties().catch(() => ({ data: [] })),
        getTrashedCollingsProperties().catch(() => ({ data: [] })),
      ]);
      const myListings = (myListingsRes.data || []).map((p) => ({
        ...p,
        _source: "my_listings",
        _title: p.title,
      }));
      const collings = (collingsRes.data || []).map((p) => ({
        ...p,
        _source: "collings",
        _title: p.headline,
      }));
      const merged = [...myListings, ...collings].sort(
        (a, b) => new Date(b.deleted_at) - new Date(a.deleted_at)
      );
      setTrashed(merged);
    } catch {
      toast.error("Failed to load trash");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(`${confirmRestore._source}-${confirmRestore.id}`);
    try {
      if (confirmRestore._source === "collings") {
        await restoreCollingsProperty(confirmRestore.id);
      } else {
        await restoreProperty(confirmRestore.id);
      }
      toast.success(`"${confirmRestore._title}" restored successfully`);
      setTrashed((prev) =>
        prev.filter(
          (p) => !(p.id === confirmRestore.id && p._source === confirmRestore._source)
        )
      );
    } catch {
      toast.error("Failed to restore property");
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  };

  const urgencyColor = (days) => {
    if (days <= 7) return "text-red-600 bg-red-50 border-red-200";
    if (days <= 30) return "text-orange-600 bg-orange-50 border-orange-200";
    return "text-gray-500 bg-gray-50 border-gray-200";
  };

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] min-h-screen -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6">
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/properties")}
              className="w-10 h-10 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
            >
              <ArrowLeft className="w-5 h-5 text-[#374151]" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-xl">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-black text-[#374151] tracking-tighter">Property Trash</h1>
                <p className="text-xs text-gray-400 font-medium">Listings are permanently deleted after 180 days</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 md:px-8 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#55C5D0]" />
          </div>
        ) : trashed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-400">Trash is empty</h3>
            <p className="text-sm text-gray-300 mt-1">Deleted listings will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
              {trashed.length} {trashed.length === 1 ? "listing" : "listings"} in trash
            </p>
            {trashed.map((prop) => (
              <div
                key={`${prop._source}-${prop.id}`}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-[#374151] truncate">{prop._title}</p>
                    <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500">
                      {prop._source === "collings" ? "Collings Properties" : "My Listings"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{prop.address}</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Deleted {new Date(prop.deleted_at).toLocaleDateString("en-AU")}
                  </p>
                  {prop.deleted_by && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Deleted by: <span className="font-medium text-gray-500">{prop.deleted_by}</span>
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border ${urgencyColor(prop.days_until_permanent_deletion)}`}>
                    {prop.days_until_permanent_deletion <= 7 && <AlertTriangle className="w-3 h-3" />}
                    {prop.days_until_permanent_deletion}d left
                  </span>
                  <button
                    onClick={() => setConfirmRestore(prop)}
                    disabled={restoring === `${prop._source}-${prop.id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-[#55C5D0] hover:bg-[#4AADB7] text-white rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Restore
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmRestore}
        onClose={() => setConfirmRestore(null)}
        onConfirm={handleRestore}
        loading={restoring !== null}
        title="Restore Listing"
        description={`Restore "${confirmRestore?._title}" back to your active listings?`}
        confirmText="Restore"
        variant="primary"
      />
    </div>
  );
}
